import { useEffect, useRef, useState } from 'react'

import { preview } from '@/components/form/preview'
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { Button, Card, Stack } from '@mui/material'
import { Chip } from '@workwave-tidal/core/components'

import type { TerritoryDetails } from './useTerritoriesDetails'
import { useTexts } from './useTexts'

interface Props {
  detail: TerritoryDetails
  driversAndVehiclesData: uui.domain.client.rm.TerritoriesDriversAndVehicles
}

const DEFAULT_HEIGHT_OF_TWO_ROWS = 56

export function TerritoryDetail(props: Props) {
  const { detail, driversAndVehiclesData } = props
  const texts = useTexts()
  const vehiclesList = driversAndVehiclesData?.[detail.id]?.vehicles
  const driversList = driversAndVehiclesData?.[detail.id]?.drivers

  const [showAllDrivers, setShowAllDrivers] = useState(false)
  const [showAllVehicles, setShowAllVehicles] = useState(false)
  const [isVehiclesOverflowing, setIsVehiclesOverflowing] = useState(false)
  const [isDriversOverflowing, setIsDriversOverflowing] = useState(false)

  const vehiclesContainerRef = useRef<HTMLDivElement>(null)
  const driversContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Check if the vehicles container height exceeds default height of 2 rows
    if (vehiclesContainerRef.current) {
      setIsVehiclesOverflowing(
        vehiclesContainerRef.current.scrollHeight > DEFAULT_HEIGHT_OF_TWO_ROWS,
      )
    }

    // Check if the drivers container height exceeds default height of 2 rows
    if (driversContainerRef.current) {
      setIsDriversOverflowing(driversContainerRef.current.scrollHeight > DEFAULT_HEIGHT_OF_TWO_ROWS)
    }
  }, [detail.vehicleIds.length, detail.driverIds.length])

  return (
    <Card variant="outlined" sx={{ padding: 2 }} data-testid="courier-territory">
      <Stack direction="column" spacing={1}>
        <preview.SectionTitle>{detail.name}</preview.SectionTitle>

        {/* Vehicles */}
        <preview.Field
          label={`${texts.vehiclesAccess}${!detail.hasAllVehicles ? ` (${detail.vehicleIds.length})` : ''}`}
          variant="alternative"
        >
          {detail.hasAllVehicles ? (
            texts.allVehicles
          ) : (
            <>
              <Stack
                gap={0.5}
                direction="row"
                flexWrap="wrap"
                ref={vehiclesContainerRef}
                sx={{
                  maxHeight: showAllVehicles ? 'none' : `${DEFAULT_HEIGHT_OF_TWO_ROWS}px`,
                  overflow: 'hidden',
                  transition: 'max-height 0.3s ease',
                }}
              >
                {detail.vehicleIds.map(vehicleId => (
                  <Chip key={vehicleId} label={vehiclesList[vehicleId]} size="small" />
                ))}
              </Stack>
              {isVehiclesOverflowing && (
                <Button
                  size="small"
                  disableRipple
                  sx={{
                    textAlign: 'left',
                    justifyContent: 'flex-start',
                    width: '100%',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      boxShadow: 'none',
                    },
                  }}
                  onClick={() => setShowAllVehicles(!showAllVehicles)}
                >
                  <Stack gap={1} direction="row" alignItems="center" textAlign="center">
                    {showAllVehicles ? (
                      <>
                        {texts.showLess} <KeyboardArrowUp color="primary" fontSize="small" />
                      </>
                    ) : (
                      <>
                        {texts.showMore} <KeyboardArrowDown color="primary" fontSize="small" />
                      </>
                    )}
                  </Stack>
                </Button>
              )}
            </>
          )}
        </preview.Field>

        {/* Drivers */}
        <preview.Field
          label={`${texts.driversAccess}${!detail.hasAllDrivers ? ` (${detail.driverIds.length})` : ''}`}
          variant="alternative"
        >
          {detail.hasAllDrivers ? (
            texts.allDrivers
          ) : (
            <>
              <Stack
                gap={0.5}
                direction="row"
                flexWrap="wrap"
                ref={driversContainerRef}
                sx={{
                  maxHeight: showAllDrivers ? 'none' : `${DEFAULT_HEIGHT_OF_TWO_ROWS}px`,
                  overflow: 'hidden',
                  transition: 'max-height 0.3s ease',
                }}
              >
                {detail.driverIds.map(driverId => (
                  <Chip key={driverId} label={driversList[driverId]} size="small" />
                ))}
              </Stack>
              {isDriversOverflowing && (
                <Button
                  disableRipple
                  sx={{
                    textAlign: 'left',
                    justifyContent: 'flex-start',
                    width: '100%',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      boxShadow: 'none',
                    },
                  }}
                  size="small"
                  onClick={() => setShowAllDrivers(!showAllDrivers)}
                >
                  <Stack gap={1} direction="row" alignItems="center" textAlign="center">
                    {showAllDrivers ? (
                      <>
                        {texts.showLess} <KeyboardArrowUp color="primary" fontSize="small" />
                      </>
                    ) : (
                      <>
                        {texts.showMore} <KeyboardArrowDown color="primary" fontSize="small" />
                      </>
                    )}
                  </Stack>
                </Button>
              )}
            </>
          )}
        </preview.Field>
      </Stack>
    </Card>
  )
}
